import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import { push, Routes } from 'routing';
import { getPlanKeyFeatures, getPlanPriceString } from 'services/plans/utils';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { PlanDefinitions, isSubscriptionPlan } from 'common/modules/plans';
import { RiCheckLine } from 'react-icons/ri';
import { useTranslation } from 'services/localization';
import { useLocation } from 'react-router-dom';
import { BILLING_CURRENCY } from 'common/modules/billing';

const PlanInformation = () => {
	const shopPlan = useSelector(ShopSelectors.shopPlan)!;
	const planDefinition = PlanDefinitions[shopPlan];
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const shopFeatures = useSelector(ShopSelectors.shopFeatures);
	const hasCoreAdvancedDiscount =
		shopPlan === 'ADVANCED' &&
		shopFeatures?.plan.campaign === 'core_commission_to_advanced_with_grow_price';

	const keyFeatures = getPlanKeyFeatures(shopPlan, t).filter((f) => !f.hideInPreview);

	const renderDefaultPlanPriceText = () => (
		<Typography color="secondary" sx={{ fontSize: '1.4rem' }}>
			{t('account.billing.plan.pricing', {
				price: getPlanPriceString(shopPlan, BILLING_CURRENCY),
				defaultValue: '{{price}} /mo/store',
			})}
		</Typography>
	);

	const renderCoreAdvancedDiscountText = () => (
		<>
			<Typography
				color="secondary"
				sx={{ fontSize: '1.4rem' }}
				style={{ textDecoration: 'line-through' }}
			>
				{t('account.billing.plan.pricing', {
					price: getPlanPriceString(shopPlan, BILLING_CURRENCY),
					defaultValue: '{{price}} /mo/store',
				})}
			</Typography>
			<Typography color="secondary" sx={{ fontSize: '1.4rem' }}>
				{t('account.billing.plan.pricing', {
					price: getPlanPriceString('GROW', BILLING_CURRENCY),
					defaultValue: '{{price}} /mo/store',
				})}{' '}
				<span
					style={{
						fontWeight: 'bold',
						fontSize: '1.1rem',
						verticalAlign: 'text-bottom',
					}}
				>
					(
					{t(
						'account.billing.plan.coreAdvancedDiscount',
						'offer for Rentle Core customers until 8.6.2023',
					)}
					)
				</span>
			</Typography>
		</>
	);

	return (
		<ContainerBox>
			<Typography variant="h6">{t('account.billing.plan.header', 'Plan')}</Typography>
			<Typography variant="body1" color="secondary">
				{t('account.billing.plan.subHeader', 'Manage or change your current plan')}
			</Typography>
			<Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: 4 }}>
				<Box>
					<Typography sx={{ fontWeight: 500 }} gutterBottom>
						{planDefinition.name}
					</Typography>
					{isSubscriptionPlan(shopPlan) && (
						<>
							{hasCoreAdvancedDiscount
								? renderCoreAdvancedDiscountText()
								: renderDefaultPlanPriceText()}
						</>
					)}
				</Box>
				<Button onClick={() => push(Routes.PLANS, { from: pathname })} color="secondary">
					{t('shop.billingView.changePlan', 'Change plan')}
				</Button>
			</Box>
			<Box sx={{ mt: 2 }}>
				{keyFeatures.map((feature) => (
					<Stack direction="row" alignItems="center" sx={{ mb: 0.5 }} key={feature.title}>
						<RiCheckLine size={20} />
						<Typography color="secondary" sx={{ ml: 1, fontSize: '1.4rem' }}>
							{feature.title}
						</Typography>
					</Stack>
				))}
			</Box>
		</ContainerBox>
	);
};

export default PlanInformation;
